import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import DocumentUpload from "@/components/app/DocumentUpload";
import { useSEO } from "@/lib/seo";
import { trackPreapprovalStarted, trackPreapprovalCompleted } from "@/lib/analytics";

const USER_DOC_TYPES = [
  { value: "drivers_license", label: "Driver's License" },
  { value: "proof_of_income", label: "Proof of Income" },
  { value: "proof_of_insurance", label: "Proof of Insurance" },
  { value: "proof_of_residence", label: "Proof of Residence" },
  { value: "ssn_verification", label: "SSN Verification" },
  { value: "additional", label: "Additional Supporting Documents" },
];

const EMPTY = {
  full_name: "", dob: "", ssn_last4: "", phone: "",
  address: "", city: "", state: "TX", zip_code: "",
  employment: { employer: "", role: "", monthly_income: 0, time_on_job_months: 0, employment_type: "full_time" },
  residence: { rent_or_own: "rent", monthly_payment: 0, time_at_address_months: 0 },
  vehicle_preferences: { max_budget: 15000, body_type: "sedan", year_min: 2017, year_max: 2024, max_mileage: 100000, color_preference: "" },
  credit_consent: false,
  consent_signed_name: "",
};

export default function BuyerApplication() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useSEO({
    title: "Apply for Vehicle Financing — Free Manual Credit Review | FinanceLocal",
    description: "Apply for vehicle financing on FinanceLocal. Manual credit review — no automated credit-bureau pull. Get pre-approvals from investors and dealers nationwide.",
    canonical: "https://myfinancelocal.com/apply",
    noIndex: true,
  });

  useEffect(() => {
    if (!user) return;
    // Landing on a fresh apply page = user has entered the pre-approval funnel.
    trackPreapprovalStarted({ from_role: user.role });
    api.get("/applications/mine")
      .then((r) => setExisting(r.data.application))
      .catch(() => {});
    setForm((f) => ({ ...f, consent_signed_name: user.name || "" }));
  }, [user]);

  const setField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const parts = path.split(".");
      let target = next;
      for (let i = 0; i < parts.length - 1; i++) { target[parts[i]] = { ...target[parts[i]] }; target = target[parts[i]]; }
      target[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const submit = async () => {
    if (!form.credit_consent) { toast.error("You must consent to a manual credit review"); return; }
    if (!form.consent_signed_name.trim()) { toast.error("Sign your name to consent"); return; }
    setBusy(true);
    try {
      await api.post("/applications", form);
      toast.success("Application submitted — please upload supporting documents below");
      // Re-fetch so we drop into the docs view on this same page
      const r = await api.get("/applications/mine");
      setExisting(r.data.application);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally { setBusy(false); }
  };

  if (!user) return null;
  if (user.role !== "user") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center" data-testid="apply-wrong-role">
        <h1 className="font-display text-3xl">User accounts only</h1>
        <p className="text-muted-foreground mt-2">Your account is registered as <b>{user.role}</b>. Sign out and create a user account to apply.</p>
      </div>
    );
  }
  if (existing) {
    return (
      <ExistingApplication
        application={existing}
        onUpdated={(updated) => setExisting(updated)}
        onWithdrawn={() => { setExisting(null); navigate("/dashboard"); }}
      />
    );
  }

  return (
    <div data-testid="page-apply" className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="font-overline text-accent">Buyer Application</div>
        <h1 className="font-display text-4xl mt-2">Apply for Vehicle Financing</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Complete this application. A FinanceLocal administrator will perform a <b>Manual Credit Review</b> (no automated credit-bureau pull), then post an anonymized profile to investors and dealers for pre-approval.
        </p>
      </div>

      <Section title="Personal">
        <Grid>
          <Field label="Legal Full Name"><Input className="rounded-none" value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} data-testid="apply-name" /></Field>
          <Field label="Date of Birth"><Input type="date" className="rounded-none" value={form.dob} onChange={(e) => setField("dob", e.target.value)} data-testid="apply-dob" /></Field>
          <Field label="SSN (last 4)"><Input maxLength={4} className="rounded-none font-mono-data" value={form.ssn_last4} onChange={(e) => setField("ssn_last4", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="apply-ssn" /></Field>
          <Field label="Phone"><Input className="rounded-none" value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="apply-phone" /></Field>
        </Grid>
      </Section>

      <Section title="Address">
        <Grid>
          <Field label="Street Address" wide><Input className="rounded-none" value={form.address} onChange={(e) => setField("address", e.target.value)} data-testid="apply-address" /></Field>
          <Field label="City"><Input className="rounded-none" value={form.city} onChange={(e) => setField("city", e.target.value)} data-testid="apply-city" /></Field>
          <Field label="State"><Input className="rounded-none" value={form.state} onChange={(e) => setField("state", e.target.value.toUpperCase().slice(0, 2))} data-testid="apply-state" /></Field>
          <Field label="ZIP"><Input className="rounded-none" value={form.zip_code} onChange={(e) => setField("zip_code", e.target.value)} data-testid="apply-zip" /></Field>
        </Grid>
      </Section>

      <Section title="Employment">
        <Grid>
          <Field label="Employer"><Input className="rounded-none" value={form.employment.employer} onChange={(e) => setField("employment.employer", e.target.value)} /></Field>
          <Field label="Role / Title"><Input className="rounded-none" value={form.employment.role} onChange={(e) => setField("employment.role", e.target.value)} /></Field>
          <Field label="Monthly Income"><Input type="number" className="rounded-none font-mono-data" value={form.employment.monthly_income} onChange={(e) => setField("employment.monthly_income", Number(e.target.value) || 0)} data-testid="apply-income" /></Field>
          <Field label="Time on Job (months)"><Input type="number" className="rounded-none" value={form.employment.time_on_job_months} onChange={(e) => setField("employment.time_on_job_months", Number(e.target.value) || 0)} /></Field>
          <Field label="Employment Type">
            <Select value={form.employment.employment_type} onValueChange={(v) => setField("employment.employment_type", v)}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="full_time">Full-time</SelectItem>
                <SelectItem value="part_time">Part-time</SelectItem>
                <SelectItem value="self_employed">Self-employed</SelectItem>
                <SelectItem value="retired">Retired</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </Grid>
      </Section>

      <Section title="Residence">
        <Grid>
          <Field label="Rent or Own">
            <Select value={form.residence.rent_or_own} onValueChange={(v) => setField("residence.rent_or_own", v)}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="rent">Rent</SelectItem>
                <SelectItem value="own">Own</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Monthly Housing"><Input type="number" className="rounded-none font-mono-data" value={form.residence.monthly_payment} onChange={(e) => setField("residence.monthly_payment", Number(e.target.value) || 0)} /></Field>
          <Field label="Time at Address (months)"><Input type="number" className="rounded-none" value={form.residence.time_at_address_months} onChange={(e) => setField("residence.time_at_address_months", Number(e.target.value) || 0)} /></Field>
        </Grid>
      </Section>

      <Section title="Vehicle Preferences">
        <Grid>
          <Field label="Max Budget"><Input type="number" className="rounded-none font-mono-data" value={form.vehicle_preferences.max_budget} onChange={(e) => setField("vehicle_preferences.max_budget", Number(e.target.value) || 0)} data-testid="apply-budget" /></Field>
          <Field label="Body Type">
            <Select value={form.vehicle_preferences.body_type} onValueChange={(v) => setField("vehicle_preferences.body_type", v)}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {["sedan", "suv", "truck", "coupe", "van", "hatchback"].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Year Min"><Input type="number" className="rounded-none" value={form.vehicle_preferences.year_min} onChange={(e) => setField("vehicle_preferences.year_min", Number(e.target.value) || 0)} /></Field>
          <Field label="Year Max"><Input type="number" className="rounded-none" value={form.vehicle_preferences.year_max} onChange={(e) => setField("vehicle_preferences.year_max", Number(e.target.value) || 0)} /></Field>
          <Field label="Max Mileage"><Input type="number" className="rounded-none" value={form.vehicle_preferences.max_mileage} onChange={(e) => setField("vehicle_preferences.max_mileage", Number(e.target.value) || 0)} /></Field>
          <Field label="Color Preference"><Input className="rounded-none" value={form.vehicle_preferences.color_preference} onChange={(e) => setField("vehicle_preferences.color_preference", e.target.value)} /></Field>
        </Grid>
      </Section>

      <Section title="Manual Credit Review Consent">
        <div className="border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm">
          <div className="flex items-start gap-2">
            <Shield className="w-5 h-5 text-amber-700 dark:text-amber-300 flex-shrink-0 mt-0.5" />
            <div className="text-amber-900 dark:text-amber-100">
              <p className="font-semibold">Authorization for Manual Credit Review</p>
              <p className="mt-1">You authorize FinanceLocal to manually review your application, supporting documents you upload, and information lawfully obtained from public records for the purpose of underwriting a vehicle loan. FinanceLocal does <b>not</b> currently perform an automated consumer-reporting-agency credit pull. You may withdraw your application at any time before review begins.</p>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-start gap-3">
          <Checkbox checked={form.credit_consent} onCheckedChange={(v) => setField("credit_consent", !!v)}
            className="rounded-none mt-1" data-testid="apply-consent" />
          <div className="text-sm">
            I authorize the manual credit review described above.
          </div>
        </div>
        <div className="mt-4">
          <Label className="font-overline">Type your legal name to sign</Label>
          <Input className="rounded-none mt-2" value={form.consent_signed_name}
            onChange={(e) => setField("consent_signed_name", e.target.value)} data-testid="apply-signature" />
        </div>
      </Section>

      <Button data-testid="apply-submit" disabled={busy} onClick={submit}
        className="rounded-none uppercase tracking-wider w-full h-12 mt-6">
        {busy ? "Submitting…" : "Submit Application"}
      </Button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-8 border-t border-border pt-6">
      <div className="font-overline text-accent mb-4">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid sm:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children, wide }) {
  return (
    <div className={wide ? "sm:col-span-2" : ""}>
      <Label className="font-overline mb-2 block">{label}</Label>
      {children}
    </div>
  );
}


const APP_STATUS_LABEL = {
  submitted: "Submitted — awaiting manual credit review",
  credit_pulled: "Manual credit review complete — preparing to publish",
  published: "Published — collecting investor pre-approvals",
  preapproved: "Pre-approved by one or more investors",
  matched: "Matched with a vehicle",
  originated: "Loan originated",
  withdrawn: "Withdrawn",
  closed: "Closed",
};

function ExistingApplication({ application, onUpdated, onWithdrawn }) {
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();
  const editable = application.status === "submitted" || application.status === "draft";

  const withdraw = async () => {
    if (!window.confirm("Withdraw this application? This cannot be undone — you'll need to re-apply to receive offers.")) return;
    try {
      await api.post(`/applications/${application.application_id}/withdraw`);
      toast.success("Application withdrawn");
      onWithdrawn();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to withdraw");
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid="apply-already-submitted">
      <div className="border-b border-border pb-6 mb-6">
        <div className="font-overline text-accent">Buyer Application</div>
        <h1 className="font-display text-3xl mt-2">Application on file</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Status: <b data-testid="apply-status">{APP_STATUS_LABEL[application.status] || application.status}</b>
        </p>
        {editable && (
          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider"
              onClick={() => setEditing(true)} data-testid="apply-edit-btn">Edit Application</Button>
            <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider text-red-600 border-red-300 hover:bg-red-50"
              onClick={withdraw} data-testid="apply-withdraw-btn">Withdraw Application</Button>
          </div>
        )}
        {!editable && (
          <p className="text-xs text-muted-foreground mt-2">
            Manual credit review has begun — the application can no longer be edited or withdrawn.
          </p>
        )}
      </div>

      {editing && (
        <EditApplicationForm application={application}
          onCancel={() => setEditing(false)}
          onSaved={(u) => { setEditing(false); onUpdated(u); }} />
      )}

      <div className="border border-border p-6 mb-6">
        <DocumentUpload
          entityType="loan_application"
          entityId={application.application_id}
          title="Supporting Documents"
          docTypes={USER_DOC_TYPES}
        />
      </div>
      <Button onClick={() => navigate("/dashboard")} className="rounded-none uppercase text-xs tracking-wider">Open My Dashboard</Button>
    </div>
  );
}

function EditApplicationForm({ application, onCancel, onSaved }) {
  const [form, setForm] = useState({
    full_name: application.full_name || "",
    dob: application.dob || "",
    phone: application.phone || "",
    address: application.address || "",
    city: application.city || "",
    state: application.state || "",
    zip_code: application.zip_code || "",
    employment: { ...(application.employment || {}) },
    residence: { ...(application.residence || {}) },
    vehicle_preferences: { ...(application.vehicle_preferences || {}) },
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/applications/${application.application_id}`, form);
      toast.success("Application updated");
      onSaved(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-border p-6 mb-6" data-testid="apply-edit-form">
      <div className="font-overline text-accent mb-4">Edit Application</div>
      <Grid>
        <Field label="Legal Full Name"><Input className="rounded-none" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="edit-name" /></Field>
        <Field label="Phone"><Input className="rounded-none" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" /></Field>
        <Field label="Street Address" wide><Input className="rounded-none" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="edit-address" /></Field>
        <Field label="City"><Input className="rounded-none" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
        <Field label="State"><Input className="rounded-none" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase().slice(0, 2) })} /></Field>
        <Field label="ZIP"><Input className="rounded-none" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></Field>
        <Field label="Monthly Income"><Input type="number" className="rounded-none font-mono-data" value={form.employment.monthly_income || 0} onChange={(e) => setForm({ ...form, employment: { ...form.employment, monthly_income: Number(e.target.value) || 0 } })} /></Field>
        <Field label="Max Budget"><Input type="number" className="rounded-none font-mono-data" value={form.vehicle_preferences.max_budget || 0} onChange={(e) => setForm({ ...form, vehicle_preferences: { ...form.vehicle_preferences, max_budget: Number(e.target.value) || 0 } })} data-testid="edit-budget" /></Field>
      </Grid>
      <div className="mt-4 flex gap-2">
        <Button onClick={save} disabled={busy} className="rounded-none uppercase text-xs tracking-wider" data-testid="apply-edit-save">{busy ? "Saving…" : "Save Changes"}</Button>
        <Button onClick={onCancel} variant="outline" className="rounded-none uppercase text-xs tracking-wider" data-testid="apply-edit-cancel">Cancel</Button>
      </div>
    </div>
  );
}
